import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import LanguageContext from './LanguageContext';
import { headerCopy } from './translations';

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { language, setLanguage } = useContext(LanguageContext);
  const copy = headerCopy[language] || headerCopy.English;
  const location = useLocation();

  // Check if user is logged in
  useEffect(() => {
    const checkLoginStatus = () => {
      const loggedIn = localStorage.getItem('userLoggedIn') === 'true';
      setIsLoggedIn(loggedIn);
    };

    checkLoginStatus();
    // Listen for storage changes (in case user logs in/out in another tab)
    window.addEventListener('storage', checkLoginStatus);
    // Listen for custom login/logout events
    window.addEventListener('userLogin', checkLoginStatus);
    window.addEventListener('userLogout', checkLoginStatus);

    return () => {
      window.removeEventListener('storage', checkLoginStatus);
      window.removeEventListener('userLogin', checkLoginStatus);
      window.removeEventListener('userLogout', checkLoginStatus);
    };
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.classList.remove('mobile-menu-open');
  };

  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    // Prevent body scroll when mobile menu is open
    if (newState) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
  };

  // Close mobile menu when route changes
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, []);

  return (
    <header className={`app-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        {/* Left: Logo */}
        <Link to="/" className="header-logo">
          <span className="logo-mana">Mana</span>
          <span className="logo-wave">Wave</span>
        </Link>

        {/* Center: Navigation (hidden on mobile) */}
        <nav className="header-nav">
          <Link 
            to="/" 
            className={`nav-link ${isActive('/') ? 'active' : ''}`}
          >
            {copy.nav.home}
          </Link>
          <Link 
            to="/services" 
            className={`nav-link ${isActive('/services') ? 'active' : ''}`}
          >
            {copy.nav.services}
          </Link>
          <Link 
            to="/about" 
            className={`nav-link ${isActive('/about') ? 'active' : ''}`}
          >
            {copy.nav.about}
          </Link>
          <Link 
            to="/terms" 
            className={`nav-link ${isActive('/terms') ? 'active' : ''}`}
          >
            {copy.nav.terms}
          </Link>
          {isLoggedIn && (
          <Link 
            to="/my-bookings" 
            className={`nav-link ${isActive('/my-bookings') ? 'active' : ''}`}
          >
            {copy.nav.myBookings}
          </Link>
          )}
        </nav>

        {/* Right: Actions */}
        <div className="header-actions">
          <div className="language-selector">
            <label htmlFor="language-select" className="sr-only">
              Choose language
            </label>
            <select
              id="language-select"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="Arabic">Arabic (العربية)</option>
              <option value="English">English (English)</option>
              <option value="French">French (Français)</option>
              <option value="German">German (Deutsch)</option>
              <option value="Russian">Russian (Русский)</option>
            </select>
          </div>
          {/* Auth Link (shown on desktop) - only if not logged in */}
          {!isLoggedIn && (
          <div className="header-auth-links">
            <Link 
              to="/login" 
              className={`header-auth-link ${isActive('/login') ? 'active' : ''}`}
            >
              {copy.auth.login}
            </Link>
          </div>
          )}

          {/* Logout button (shown on desktop) - only if logged in */}
          {isLoggedIn && (
            <div className="header-auth-links">
              <button 
                onClick={() => {
                  localStorage.removeItem('userLoggedIn');
                  localStorage.removeItem('userId');
                  localStorage.removeItem('userEmail');
                  localStorage.removeItem('userName');
                  setIsLoggedIn(false);
                  window.dispatchEvent(new Event('userLogout'));
                  window.location.href = '/';
                }}
                className="header-auth-link"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                {copy.auth.logout}
              </button>
            </div>
          )}

          {/* Profile Icon/Avatar - Only show when logged in */}
          {isLoggedIn && (
            <Link 
              to="/profile" 
              className="header-icon-btn profile-btn" 
              aria-label="Profile"
            >
            <svg 
              width="22" 
              height="22" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            </Link>
          )}

          {/* Hamburger Menu (mobile only) */}
          <button 
            className="header-icon-btn hamburger-btn"
            onClick={toggleMobileMenu}
            aria-label="Menu"
            aria-expanded={isMobileMenuOpen}
          >
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className={isMobileMenuOpen ? 'open' : ''}
            >
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <nav className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
        <Link 
          to="/" 
          className={`mobile-nav-link ${isActive('/') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          {copy.mobile.home}
        </Link>
        <Link 
          to="/services" 
          className={`mobile-nav-link ${isActive('/services') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          {copy.mobile.services}
        </Link>
        <Link 
          to="/about" 
          className={`mobile-nav-link ${isActive('/about') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          {copy.mobile.about}
        </Link>
        <Link 
          to="/terms" 
          className={`mobile-nav-link ${isActive('/terms') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          {copy.mobile.terms}
        </Link>
        {isLoggedIn && (
          <>
        <Link 
          to="/my-bookings" 
          className={`mobile-nav-link ${isActive('/my-bookings') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
          {copy.mobile.myBookings}
        </Link>
        <Link 
              to="/profile" 
              className={`mobile-nav-link ${isActive('/profile') ? 'active' : ''}`}
          onClick={closeMobileMenu}
        >
              {copy.mobile.profile}
        </Link>
          </>
        )}
        <div className="mobile-language-selector">
          <label htmlFor="mobile-language-select" className="sr-only">
            Choose language
          </label>
          <select
            id="mobile-language-select"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="Arabic">Arabic (العربية)</option>
            <option value="English">English (English)</option>
            <option value="French">French (Français)</option>
            <option value="German">German (Deutsch)</option>
            <option value="Russian">Russian (Русский)</option>
          </select>
        </div>
        <div className="mobile-auth-links">
          {!isLoggedIn ? (
            <Link 
            to="/login" 
            className={`mobile-nav-link ${isActive('/login') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            {copy.auth.login}
          </Link>
          ) : (
              <button 
                onClick={() => {
                  localStorage.removeItem('userLoggedIn');
                  localStorage.removeItem('userId');
                  localStorage.removeItem('userEmail');
                  localStorage.removeItem('userName');
                  setIsLoggedIn(false);
                  window.dispatchEvent(new Event('userLogout'));
                  closeMobileMenu();
                  window.location.href = '/';
                }}
                className="mobile-nav-link"
                style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
              >
                {copy.auth.logout}
              </button>
          )}
        </div>
      </nav>
    </header>
  );
}

export default Header;

